import maplibregl, {
  type GeoJSONSource,
  type LngLatBoundsLike,
  type MapGeoJSONFeature,
  type StyleSpecification,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import type { DecisionFeature, DecisionFeatureCollection, DecisionProps } from "./api.js";

/** Outcome encoding: colorblind-safe via BOTH a distinct color and shape. */
export type MarkerShape = "circle" | "triangle" | "square";
export interface OutcomeStyle {
  label: string;
  color: string;
  shape: MarkerShape;
  iconId: string;
}
export const PALETTE: Record<string, OutcomeStyle> = {
  APPROVED: { label: "Approved", color: "#1a9850", shape: "circle", iconId: "mk-approved" },
  REFUSED: { label: "Refused", color: "#d73027", shape: "triangle", iconId: "mk-refused" },
  OTHER: { label: "Other", color: "#4d4d4d", shape: "square", iconId: "mk-other" },
};

// Singapore extent — keeps the map from panning off into the ocean.
const SG_BOUNDS: LngLatBoundsLike = [
  [103.55, 1.13],
  [104.12, 1.49],
];
const SG_CENTER: [number, number] = [103.8198, 1.3521];

const ONEMAP_STYLE: StyleSpecification = {
  version: 8,
  // Glyphs are required for any symbol `text-field` — without them the cluster
  // count numbers silently fail to render (clusters look like blank circles).
  glyphs: "https://fonts.openmaptiles.org/{fontstack}/{range}.pbf",
  sources: {
    onemap: {
      type: "raster",
      tiles: ["https://www.onemap.gov.sg/maps/tiles/Default/{z}/{x}/{y}.png"],
      tileSize: 256,
      minzoom: 11,
      maxzoom: 19,
      attribution:
        '<a href="https://www.onemap.gov.sg/" target="_blank" rel="noopener">OneMap</a> ' +
        "&copy; Singapore Land Authority",
    },
  },
  layers: [{ id: "onemap-tiles", type: "raster", source: "onemap" }],
};

const SOURCE_ID = "decisions";

// Rasterize icons at a high pixel ratio so they stay crisp even when scaled up
// (the refused triangle renders at icon-size 1.5). Display size is width/ratio,
// so raising the ratio adds source pixels without changing the on-screen size.
const ICON_RATIO = 6;

/** Rasterize a small colored, white-outlined marker of the given shape. */
function makeIcon(shape: MarkerShape, color: string, size = 22, ratio = ICON_RATIO): ImageData {
  const px = size * ratio;
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context unavailable");
  const c = px / 2;
  const r = px * 0.34;
  ctx.lineWidth = 2 * ratio;
  ctx.strokeStyle = "#ffffff";
  ctx.fillStyle = color;
  ctx.beginPath();
  if (shape === "circle") {
    ctx.arc(c, c, r, 0, Math.PI * 2);
  } else if (shape === "triangle") {
    ctx.moveTo(c, c - r);
    ctx.lineTo(c + r, c + r);
    ctx.lineTo(c - r, c + r);
  } else {
    ctx.rect(c - r, c - r, r * 2, r * 2);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  return ctx.getImageData(0, 0, px, px);
}

function esc(value: unknown): string {
  return String(value ?? "—")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function popupTable(p: DecisionProps): string {
  const outcome = p.decision_type_norm ? PALETTE[p.decision_type_norm]?.label ?? p.decision_type_norm : "—";
  const rows: Array<[string, unknown]> = [
    ["Address", p.address],
    ["Decision date", p.decision_date],
    ["Outcome", outcome],
    ["Decision type", p.decision_type],
    ["Development type", p.appl_type],
    ["Submission no.", p.submission_no],
    ["Decision no.", p.decision_no],
    ["Description", p.submission_desc],
    ["Lot no.", p.mkts_lotno],
    ["District", p.postal_district],
    ["Postal code", p.postal_code],
  ];
  const body = rows
    .map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`)
    .join("");
  // Rendered vector features can hand booleans back as strings, so normalize.
  const hasHistory = p.has_history === true || String(p.has_history) === "true";
  const history = hasHistory
    ? `<p class="popup-history">This decision has earlier versions (v${esc(p.version)} current).</p>`
    : "";
  return `<table>${body}</table>${history}`;
}

function popupHtml(p: DecisionProps): string {
  return `<div class="popup">${popupTable(p)}</div>`;
}

/** Popup body for several decisions sharing one location. */
function popupListHtml(list: DecisionProps[]): string {
  const items = list.map((p) => `<div class="popup-item">${popupTable(p)}</div>`).join("");
  return `<div class="popup popup-multi"><p class="popup-count">${list.length} decisions at this location</p>${items}</div>`;
}

/** Read DecisionProps off rendered/leaf features, de-duplicated by row id. */
function dedupeProps(features: Array<{ properties: unknown }>): DecisionProps[] {
  const seen = new Set<string>();
  const out: DecisionProps[] = [];
  for (const f of features) {
    const p = f.properties as DecisionProps;
    const key = String(p.id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

function makePopup(list: DecisionProps[]): maplibregl.Popup {
  return list.length > 1
    ? new maplibregl.Popup({ maxWidth: "340px", className: "list-popup" })
    : new maplibregl.Popup({ maxWidth: "320px" });
}

const EMPTY_FC: DecisionFeatureCollection = {
  type: "FeatureCollection",
  features: [],
  unmappable_count: 0,
  unmappable: [],
  truncated: false,
};

export class MapController {
  private readonly map: maplibregl.Map;
  private ready = false;
  private pending: DecisionFeatureCollection | null = null;

  constructor(container: HTMLElement, initialCenter?: [number, number], initialZoom?: number) {
    this.map = new maplibregl.Map({
      container,
      style: ONEMAP_STYLE,
      center: initialCenter ?? SG_CENTER,
      zoom: initialZoom ?? 11,
      minZoom: 10,
      maxZoom: 18,
      maxBounds: SG_BOUNDS,
    });
    this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-left");
    this.map.on("load", () => this.onLoad());
  }

  private onLoad(): void {
    for (const style of Object.values(PALETTE)) {
      const img = makeIcon(style.shape, style.color);
      this.map.addImage(style.iconId, img, { pixelRatio: ICON_RATIO });
    }

    this.map.addSource(SOURCE_ID, {
      type: "geojson",
      data: this.pending ?? EMPTY_FC,
      cluster: true,
      clusterRadius: 50,
      clusterMaxZoom: 14,
    });

    this.map.addLayer({
      id: "clusters",
      type: "circle",
      source: SOURCE_ID,
      filter: ["has", "point_count"],
      paint: {
        "circle-color": "#3b7dd8",
        "circle-opacity": 0.85,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
        "circle-radius": ["step", ["get", "point_count"], 16, 25, 22, 100, 30],
      },
    });
    this.map.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: SOURCE_ID,
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-size": 13,
        "text-font": ["Open Sans Bold"],
      },
      paint: { "text-color": "#ffffff" },
    });
    this.map.addLayer({
      id: "points",
      type: "symbol",
      source: SOURCE_ID,
      filter: ["!", ["has", "point_count"]],
      layout: {
        "icon-image": [
          "match",
          ["get", "decision_type_norm"],
          "APPROVED", PALETTE.APPROVED.iconId,
          "REFUSED", PALETTE.REFUSED.iconId,
          PALETTE.OTHER.iconId,
        ],
        // All markers rendered 3x larger than the original base size.
        "icon-size": 1.5,
        "icon-allow-overlap": true,
      },
    });

    // Cluster click: zoom in to split it — unless its members are all at the
    // same coordinate (zoom can't separate those), in which case list them.
    this.map.on("click", "clusters", (e) => {
      const feature = this.map.queryRenderedFeatures(e.point, { layers: ["clusters"] })[0];
      if (!feature || feature.geometry.type !== "Point") return;
      const clusterId = feature.properties?.cluster_id as number | undefined;
      const source = this.map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
      if (clusterId === undefined || !source) return;
      const center = feature.geometry.coordinates as [number, number];
      void source.getClusterLeaves(clusterId, 200, 0).then((leaves) => {
        const coincident = leaves.every(
          (l) =>
            l.geometry.type === "Point" &&
            Math.abs((l.geometry.coordinates as [number, number])[0] - center[0]) < 1e-5 &&
            Math.abs((l.geometry.coordinates as [number, number])[1] - center[1]) < 1e-5,
        );
        if (coincident && leaves.length > 1) {
          const props = dedupeProps(leaves);
          makePopup(props).setLngLat(center).setHTML(popupListHtml(props)).addTo(this.map);
        } else {
          void source.getClusterExpansionZoom(clusterId).then((zoom) => {
            this.map.easeTo({ center, zoom: Math.min(zoom, this.map.getMaxZoom()) });
          });
        }
      });
    });

    // Hover: show a transient (non-interactive) popup that follows the marker
    // under the cursor. Reuses one Popup instance and updates it on mousemove.
    const hoverPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      maxWidth: "320px",
      className: "hover-popup",
    });
    this.map.on("mousemove", "points", (e) => {
      const feature = e.features?.[0] as MapGeoJSONFeature | undefined;
      if (!feature || feature.geometry.type !== "Point") return;
      this.map.getCanvas().style.cursor = "pointer";
      const stacked = dedupeProps(this.map.queryRenderedFeatures(e.point, { layers: ["points"] }));
      const props = stacked[0] ?? (feature.properties as DecisionProps);
      const more =
        stacked.length > 1
          ? `<p class="popup-more">+${stacked.length - 1} more at this location — click to see all</p>`
          : "";
      hoverPopup
        .setLngLat(feature.geometry.coordinates as [number, number])
        .setHTML(`<div class="popup">${popupTable(props)}${more}</div>`)
        .addTo(this.map);
    });
    this.map.on("mouseleave", "points", () => {
      this.map.getCanvas().style.cursor = "";
      hoverPopup.remove();
    });

    // Click: pin a closeable popup. If several markers are stacked at the same
    // spot (multiple decisions at one address), list them all. Also the way to
    // see details on touch, where hover isn't available.
    this.map.on("click", "points", (e) => {
      const stacked = this.map.queryRenderedFeatures(e.point, { layers: ["points"] });
      const props = dedupeProps(stacked);
      if (props.length === 0) return;
      const first = stacked[0] as MapGeoJSONFeature;
      const center =
        first.geometry.type === "Point"
          ? (first.geometry.coordinates as [number, number])
          : (e.lngLat.toArray() as [number, number]);
      const html = props.length > 1 ? popupListHtml(props) : popupHtml(props[0] as DecisionProps);
      makePopup(props).setLngLat(center).setHTML(html).addTo(this.map);
    });

    // Clusters just get a pointer cursor (click expands them).
    this.map.on("mouseenter", "clusters", () => {
      this.map.getCanvas().style.cursor = "pointer";
    });
    this.map.on("mouseleave", "clusters", () => {
      this.map.getCanvas().style.cursor = "";
    });

    this.ready = true;
    if (this.pending) this.setDecisions(this.pending);
  }

  /** Register a debounced-by-caller handler for viewport changes. */
  onViewportChange(handler: () => void): void {
    this.map.on("moveend", handler);
  }

  /** Fire once the style/layers are ready (or immediately if already ready). */
  onReady(handler: () => void): void {
    if (this.ready) handler();
    else this.map.once("load", () => handler());
  }

  setDecisions(fc: DecisionFeatureCollection): void {
    if (!this.ready) {
      this.pending = fc;
      return;
    }
    const source = this.map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    if (source) source.setData(fc);
  }

  /** Current viewport as [minLng, minLat, maxLng, maxLat]. */
  getBbox(): [number, number, number, number] {
    const b = this.map.getBounds();
    return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
  }

  getCenter(): [number, number] {
    const c = this.map.getCenter();
    return [c.lng, c.lat];
  }

  getZoom(): number {
    return this.map.getZoom();
  }

  flyTo(lng: number, lat: number, zoom = 16): void {
    this.map.flyTo({ center: [lng, lat], zoom, essential: true });
  }

  /** Fit the viewport to the given features (used after a keyword search). */
  fitToFeatures(features: DecisionFeature[]): void {
    if (features.length === 0) return;
    const bounds = new maplibregl.LngLatBounds();
    for (const f of features) {
      if (f.geometry.type === "Point") {
        bounds.extend(f.geometry.coordinates as [number, number]);
      }
    }
    if (bounds.isEmpty()) return;
    this.map.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 600 });
  }
}
